import { useEffect, useMemo, useRef } from 'react';
import { openPanelInCanvas, updatePanelProps } from 'agentable-canvas/whiteboard';
import { useCanvasSession } from '../../contexts/CanvasSessionContext';
import { dispatchCanvasPreviewRefresh } from '../../hooks/useIframeMessaging';
import type { SessionArtifact } from '../../lib/canvas-session-types';

const PREVIEW_PANEL_ID = 'web-preview';
const PREVIEW_WIDTH = 960;
const PREVIEW_HEIGHT = 720;

/**
 * Artifact kinds that mean the draft site HTML actually changed. Chat artifacts
 * (`user-message`, `assistant-message`) and `edit-request` (instruction, not yet
 * applied) are excluded so sending a chat message never reloads the preview.
 */
const HTML_MUTATING_ARTIFACT_KINDS: ReadonlySet<SessionArtifact['kind']> = new Set([
  'generation-event',
  'edit-result',
  'publish-result',
]);

/**
 * Opens / refreshes the WebPanel when generation completes or draft artifacts update.
 */
export function DraftPreviewBridge(): null {
  const { session } = useCanvasSession();
  const openedSiteRef = useRef<string | null>(null);
  const lastHtmlRevisionRef = useRef(0);

  // Count only HTML-mutating artifacts. This is the preview's reload trigger, so
  // chat/message artifacts must not affect it (otherwise every chat message would
  // bump refreshKey and reload the iframe).
  const htmlRevision = useMemo(
    () =>
      session.artifacts.reduce(
        (count, artifact) =>
          HTML_MUTATING_ARTIFACT_KINDS.has(artifact.kind) ? count + 1 : count,
        0,
      ),
    [session.artifacts],
  );

  useEffect(() => {
    const siteId = session.siteId;
    if (!siteId) {
      return;
    }

    const isNewSite = openedSiteRef.current !== siteId;
    const artifactsChanged = htmlRevision !== lastHtmlRevisionRef.current;

    if (session.phase === 'complete' && isNewSite) {
      const panelRefreshKey = Date.now();
      const synced = updatePanelProps(PREVIEW_PANEL_ID, {
        siteId,
        refreshKey: panelRefreshKey,
      });
      if (!synced) {
        openPanelInCanvas(PREVIEW_PANEL_ID, {
          focus: true,
          size: { w: PREVIEW_WIDTH, h: PREVIEW_HEIGHT },
          panelProps: {
            __title: 'Draft preview',
            __hideChrome: true,
            __noBorder: true,
            __fullBleed: true,
            siteId,
            refreshKey: panelRefreshKey,
          },
        });
      }
      dispatchCanvasPreviewRefresh({ siteId, reason: 'site-switch' });
      openedSiteRef.current = siteId;
      lastHtmlRevisionRef.current = htmlRevision;
      return;
    }

    if (openedSiteRef.current === siteId && artifactsChanged) {
      updatePanelProps(PREVIEW_PANEL_ID, { siteId, refreshKey: Date.now() });
      dispatchCanvasPreviewRefresh({ siteId, reason: 'artifact-update' });
      lastHtmlRevisionRef.current = htmlRevision;
    }
  }, [session.siteId, session.phase, htmlRevision]);

  return null;
}
